/**
 * Wishlist Modal functionality for Collection Tabs
 * Handles modal open/close, swiper initialization, and variant selection
 */

class WishlistModal {
  constructor() {
    this.activeModal = null;
    this.swiperInstance = null;
    this.init();
    this.bfBadgeTagText = 'BF TAKE 50% OFF';
  }

  init() {
    // Listen for clicks on backupWishlistButton - always open new wishlist modal
    // Use capture phase to intercept event before other listeners
    document.addEventListener('click', (e) => {
      const backupWishlistButton = e.target.closest('.backupWishlistButton');
      
      if (backupWishlistButton) {
        // Always prevent default behavior and open the new modal
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation(); // Prevent other listeners from firing
        
        // Get the list-card-product or card-product element
        const listCardProduct = backupWishlistButton.closest('list-card-product, .card-product');
        if (!listCardProduct) return;
        
        // Get product handle from the link
        const productLink = listCardProduct.querySelector('.card-info-link, a[href*="/products/"]');
        if (!productLink) return;
        
        const href = productLink.getAttribute('href');
        // Extract handle from URL like /products/handle or /collections/collection/products/handle
        const handleMatch = href.match(/\/products\/([^/?]+)/);
        if (!handleMatch) return;
        
        const productHandle = handleMatch[1];
        
        // Open the modal
        this.openModal(productHandle);
        return;
      }
    }, true); // Use capture phase to catch event first
    
    // Listen for clicks on product cards in wishlist lightbox mode
    document.addEventListener('click', (e) => {
      // Don't trigger modal if clicking on buttons or other interactive elements (check first)
      if (e.target.closest('button, input, select, textarea, .backupWishlistButton, .cartButton')) {
        return;
      }
      
      // Check if we're in a wishlist-lightbox enabled tab that is visible
      const tabContent = e.target.closest('.tab-content.wishlist-lightbox.visible');
      
      // If not in a visible wishlist-lightbox tab, allow default behavior (navigate to product)
      if (!tabContent) return;
      
      // Check if click is on a list-card-product element
      const listCardProduct = e.target.closest('list-card-product');
      if (!listCardProduct) return;
      
      // Get the card-product inside
      const productCard = listCardProduct.querySelector('.card-product');
      if (!productCard) return;
      
      // Prevent default link behavior and open modal
      e.preventDefault();
      e.stopPropagation();
      
      // Get product handle from the link
      const productLink = listCardProduct.querySelector('.card-info-link');
      if (!productLink) return;
      
      const href = productLink.getAttribute('href');
      // Extract handle from URL like /products/handle or /collections/collection/products/handle
      const handleMatch = href.match(/\/products\/([^/?]+)/);
      if (!handleMatch) return;
      
      const productHandle = handleMatch[1];
      
      // Find and open the modal
      this.openModal(productHandle);
    });
    
    // Listen for close button clicks
    document.addEventListener('click', (e) => {
      const closeButton = e.target.closest('.wishlist-modal__close');
      if (closeButton) {
        e.preventDefault();
        e.stopPropagation();
        this.closeModal();
      }
    });
    
    // Listen for overlay clicks
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('wishlist-modal__overlay')) {
        e.preventDefault();
        e.stopPropagation();
        this.closeModal();
      }
    });
    
    // Listen for ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.activeModal) {
        this.closeModal();
      }
    });
    
    // Listen for show more button clicks
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('wishlist-modal__show-more')) {
        this.toggleDescription(e.target);
      }
    });
    
    // Listen for variant selection changes
    document.addEventListener('change', (e) => {
      if (e.target.closest('.wishlist-modal__variants')) {
        this.handleVariantChange(e.target);
      }
    });
    
    // Listen for wishlist button clicks
    document.addEventListener('click', (e) => {
      if (e.target.closest('.wishlist-modal__button.selectForWishlist')) {
        this.handleAddToWishlist(e.target.closest('.wishlist-modal__button'));
      }
    });
  }
  
  areRequiredVariantOptionsSelected(modal) {
    const variantsContainer = modal.querySelector('.wishlist-modal__variants');
    
    // If no variants container or it's hidden, no selection is required
    if (!variantsContainer || variantsContainer.style.display === 'none') {
      return true;
    }
    
    // Check all fieldsets for checked radios
    const fieldsets = variantsContainer.querySelectorAll('.wishlist-modal__variant-fieldset');
    
    for (let fieldset of fieldsets) {
      const hasCheckedRadio = fieldset.querySelector('input:checked');
      if (!hasCheckedRadio) {
        return false;
      }
    }
    
    return true;
  }
  
  getVariantSelectionMessage(modal) {
    const variantsContainer = modal.querySelector('.wishlist-modal__variants');
    
    if (!variantsContainer) {
      return 'Please select a product option.';
    }
    
    let unselectedOptions = [];
    const fieldsets = variantsContainer.querySelectorAll('.wishlist-modal__variant-fieldset');
    
    fieldsets.forEach(fieldset => {
      const legend = fieldset.querySelector('.wishlist-modal__variant-label');
      const hasCheckedRadio = fieldset.querySelector('input:checked');
      
      if (!hasCheckedRadio && legend) {
        unselectedOptions.push(legend.textContent.trim());
      }
    });
    
    if (unselectedOptions.length > 0) {
      // Create specific message based on unselected options
      if (unselectedOptions.length === 1) {
        return `Pick a ${unselectedOptions[0]}!`;
      } else {
        const lastOption = unselectedOptions.pop();
        return `Pick ${unselectedOptions.join(', ')} and ${lastOption}!`;
      }
    }
    
    // Fallback message
    return 'Please select a product option.';
  }
  
  handleErrorMessage(modal, errorMessage = false) {
    let errorWrapper = modal.querySelector('.wishlist-modal__error-message');
    
    if (!errorWrapper) {
      // Create error message element if it doesn't exist
      errorWrapper = document.createElement('div');
      errorWrapper.className = 'wishlist-modal__error-message';
      errorWrapper.style.cssText = `
        color: #dc3545;
        background: #f8d7da;
        border: 1px solid #f5c6cb;
        padding: 12px 16px;
        margin-bottom: 16px;
        border-radius: 4px;
        font-size: 14px;
        display: none;
      `;
      
      // Insert before the wishlist button
      const button = modal.querySelector('.selectForWishlist');
      if (button && button.parentNode) {
        button.parentNode.insertBefore(errorWrapper, button);
      }
    }
    
    if (errorMessage) {
      errorWrapper.textContent = errorMessage;
      errorWrapper.style.display = 'block';
    } else {
      errorWrapper.style.display = 'none';
    }
  }

  async openModal(productHandle) {
    const modal = document.getElementById('CollectionTabsWishlistModal');
    
    if (!modal) {
      console.error('Wishlist modal not found');
      return;
    }
    
    // Close any existing modal first
    if (this.activeModal) {
      this.closeModal();
    }
    
    // Fetch product data using handle
    try {
      const response = await fetch(`/products/${productHandle}.js`);
      
      if (!response.ok) {
        throw new Error(`Product not found: ${productHandle}`);
      }
      
      const product = await response.json();
      
      this.populateModal(modal, product);
      
      this.activeModal = modal;
      modal.style.display = 'flex';
      modal.classList.add('active');
      document.body.classList.add('wishlist-modal-open');
      
      // Reset scroll position to top
      const modalContainer = modal.querySelector('.wishlist-modal__container');
      const modalInfoScroll = modal.querySelector('.wishlist-modal__info-scroll');
      if (modalContainer) {
        modalContainer.scrollTop = 0;
      }
      if (modalInfoScroll) {
        modalInfoScroll.scrollTop = 0;
      }
      
      // Initialize Swiper after modal is visible and DOM is updated
      // Use requestAnimationFrame to ensure DOM is fully rendered
      // Also wait for Swiper library to be available
      this.waitForSwiper(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            this.initSwiper(modal);
          }, 150);
        });
      });
      
    } catch (error) {
      console.error('Error loading product:', error);
    }
  }
  
  populateModal(modal, product) {
    // Reset error message
    this.handleErrorMessage(modal, false);
    
    // Reset button state
    const button = modal.querySelector('.selectForWishlist');
    if (button) {
      button.classList.remove('loading');
      button.setAttribute('aria-busy', 'false');
      button.disabled = false;
      button.style.background = '';
      const svg = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
      </svg>`;
      button.innerHTML = svg + ' Add to Wishlist';
    }
    
    // Extract title and subtitle
    const titleParts = product.title.split(' - ');
    const title = titleParts[0];
    const subtitle = titleParts.slice(1).join(' - ');
    
    // Update title and subtitle
    modal.querySelector('.wishlist-modal__title').textContent = title;
    const subtitleEl = modal.querySelector('.wishlist-modal__subtitle');
    if (subtitle) {
      subtitleEl.textContent = subtitle;
      subtitleEl.style.display = 'block';
    } else {
      subtitleEl.style.display = 'none';
    }
    
    // Update badge
    const badge = this.getProductBadge(product);
    const badgeEl = modal.querySelector('.wishlist-modal__badge');
    if (badge && false) {
      badgeEl.textContent = badge.text;
      if (badge.color) {
        badgeEl.style.backgroundColor = badge.color;
      }
      badgeEl.style.display = 'inline-block';
    } else {
      badgeEl.style.display = 'none';
    }
    
    // Update price
    this.updatePrice(modal, product);
    
    // Update images
    this.updateGallery(modal, product);
    
    // Update variants
    if (product.variants.length > 1) {
      this.updateVariants(modal, product);
    } else {
      modal.querySelector('.wishlist-modal__variants').style.display = 'none';
      modal.querySelector('.wishlist-modal__size-message').style.display = 'none';
      // Set single variant data on button
      const button = modal.querySelector('.selectForWishlist');
      button.dataset.productId = product.id;
      button.dataset.variantId = product.variants[0].id;
      button.dataset.productTitle = product.title;
      button.dataset.handle = product.handle;
      button.dataset.image = product.featured_image;
      button.dataset.price = product.variants[0].price;
      button.dataset.variantTitle = product.variants[0].title;
    }
    
    // Update description
    if (product.description) {
      this.updateDescription(modal, product.description);
    } else {
      modal.querySelector('.wishlist-modal__description').style.display = 'none';
    }
  }
  
  getProductBadge(product) {
    // Check tags for badges
    for (const tag of product.tags) {
      if (tag.includes('badge:')) {
        const parts = tag.split(':');
        return {
          text: parts[1],
          color: parts[2] || null
        };
      }
      if (tag === 'restocking') {
        return { text: 'Restocking', color: null };
      }
      if (tag === 'soon') {
        return { text: 'Coming Soon', color: null };
      }
    }
    
    // Check if product is new (within last 30 days)
    const monthAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
    const createdAt = new Date(product.created_at).getTime();
    if (createdAt >= monthAgo) {
      return { text: 'New', color: null };
    }
    
    return null;
  }
  
  updatePrice(modal, product) {
    const priceContainer = modal.querySelector('.wishlist-modal__price');
    const variant = product.variants[0];
    
    let priceHTML = `<div class="price">`;
    
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const savings = ((variant.compare_at_price - variant.price) / variant.compare_at_price * 100).toFixed(0);
      priceHTML += `
        <span class="price__regular">${this.formatMoney(variant.price)}</span>
        <span class="price__compare">${this.formatMoney(variant.compare_at_price)}</span>
        <span class="badge price__badge-sale">SAVE ${savings}%</span>
      `;
    } else {
      priceHTML += `<span class="price__regular">${this.formatMoney(variant.price)}</span>`;
    }

    priceHTML += `<span class="badge price__badge-sale" style="background-color: #E2419E; color: white;">${this.bfBadgeTagText}</span>`;
    
    priceHTML += `</div>`;
    priceContainer.innerHTML = priceHTML;
  }
  
  updateGallery(modal, product) {
    const swiperWrapper = modal.querySelector('.swiper-wrapper');
    swiperWrapper.innerHTML = '';
    
    // Filter out athlete images
    
    var images = []
    if(product.tags.includes('facet:Protein:true') || product.tags.includes('all products: supplements')){
      images = [product.images[0]];
    }else{
      images = product.images.filter(img => !img.includes('ATHLETE_'));
    }
    
    images.forEach(imageUrl => {
      const slide = document.createElement('div');
      slide.className = 'swiper-slide';
      slide.innerHTML = `
        <img 
          src="${imageUrl}"
          alt="${product.title}"
          loading="lazy"
        >
      `;
      swiperWrapper.appendChild(slide);
    });
  }
  
  updateVariants(modal, product) {
    const variantsContainer = modal.querySelector('.wishlist-modal__variants');
    const sizeMessage = modal.querySelector('.wishlist-modal__size-message');
    variantsContainer.innerHTML = '';
    variantsContainer.style.display = 'block';
    sizeMessage.style.display = 'block';
    
    // Create variant radios structure
    product.options.forEach((option, optionIndex) => {
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'wishlist-modal__variant-fieldset';
      
      const legend = document.createElement('legend');
      legend.className = 'wishlist-modal__variant-label';
      legend.textContent = option.name;
      fieldset.appendChild(legend);
      
      const optionsDiv = document.createElement('div');
      optionsDiv.className = 'wishlist-modal__variant-options';
      
      option.values.forEach((value, valueIndex) => {
        // Find matching variant
        const matchingVariant = product.variants.find(v => v.options[optionIndex] === value);
        
        const label = document.createElement('label');
        label.className = 'wishlist-modal__variant-button';
        
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = `wishlist-option-${product.id}-${option.name}`;
        input.value = value;
        input.dataset.variantId = matchingVariant ? matchingVariant.id : '';
        // Don't pre-select any variants - require user to select
        // Don't disable variants based on availability - this is for wishlist, not purchasing
        // if (matchingVariant && !matchingVariant.available) input.disabled = true;
        
        const span = document.createElement('span');
        span.textContent = value;
        
        label.appendChild(input);
        label.appendChild(span);
        optionsDiv.appendChild(label);
      });
      
      fieldset.appendChild(optionsDiv);
      variantsContainer.appendChild(fieldset);
    });
    
    // Store variants data
    variantsContainer.dataset.variants = JSON.stringify(product.variants);
    variantsContainer.dataset.productId = product.id;
    variantsContainer.dataset.productTitle = product.title;
    variantsContainer.dataset.handle = product.handle;
    variantsContainer.dataset.image = product.featured_image;
    
    // Initialize variant selection
    this.initializeVariants(modal);
  }
  
  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  waitForSwiper(callback) {
    // Check if Swiper is available
    if (typeof Swiper !== 'undefined') {
      console.log('Swiper is ready');
      callback();
      return;
    }
    
    console.log('Waiting for Swiper to load...');
    
    // Wait for Swiper to load (max 5 seconds)
    let attempts = 0;
    const maxAttempts = 50; // 50 attempts * 100ms = 5 seconds
    
    const checkSwiper = setInterval(() => {
      attempts++;
      
      if (typeof Swiper !== 'undefined') {
        console.log('Swiper loaded after', attempts * 100, 'ms');
        clearInterval(checkSwiper);
        callback();
      } else if (attempts >= maxAttempts) {
        console.error('Swiper failed to load after 5 seconds');
        clearInterval(checkSwiper);
      }
    }, 100);
  }

  closeModal() {
    if (!this.activeModal) return;
    
    this.activeModal.classList.remove('active');
    this.activeModal.style.display = 'none';
    document.body.classList.remove('wishlist-modal-open');
    
    // Destroy Swiper instance
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
    
    this.activeModal = null;
  }

  initSwiper(modal) {
    // Check if Swiper is available
    if (typeof Swiper === 'undefined') {
      console.error('Swiper library not loaded - make sure Swiper CDN is included in theme.liquid');
      return;
    }
    
    const swiperEl = modal.querySelector('.wishlist-modal-swiper');
    
    if (!swiperEl) {
      console.error('Swiper element not found in modal');
      return;
    }
    
    // Destroy existing instance if any
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
    
    // Count slides
    const slideCount = swiperEl.querySelectorAll('.swiper-slide').length;
    console.log('Swiper slide count:', slideCount);
    console.log('Swiper element:', swiperEl);
    
    if (slideCount === 0) {
      console.error('No slides found in Swiper');
      return;
    }
    
    // Initialize new Swiper instance
    // Only enable loop if there are 2 or more slides
    try {
      this.swiperInstance = new Swiper(swiperEl, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: slideCount > 1,
        pagination: {
          el: modal.querySelector('.swiper-pagination'),
          clickable: true,
        },
        navigation: {
          nextEl: modal.querySelector('.swiper-button-next'),
          prevEl: modal.querySelector('.swiper-button-prev'),
        },
        keyboard: {
          enabled: true,
        },
        grabCursor: true,
        on: {
          init: function() {
            console.log('Swiper initialized successfully');
          },
          slideChange: function() {
            console.log('Slide changed to:', this.activeIndex);
          }
        }
      });
      
      console.log('Swiper instance created:', this.swiperInstance);
      
    } catch (error) {
      console.error('Error initializing Swiper:', error);
    }
  }

  initializeVariants(modal) {
    const variantsContainer = modal.querySelector('.wishlist-modal__variants');
    
    if (!variantsContainer || variantsContainer.style.display === 'none') {
      // No variants, product has default variant only
      return;
    }
    
    // Only update if variants are selected (they won't be by default anymore)
    if (this.areRequiredVariantOptionsSelected(modal)) {
      this.updateSelectedVariant(modal);
    }
  }

  handleVariantChange(input) {
    const modal = input.closest('.wishlist-modal');
    
    if (!modal) return;
    
    // Clear error message when variant changes
    this.handleErrorMessage(modal, false);
    
    this.updateSelectedVariant(modal);
  }

  updateSelectedVariant(modal) {
    const variantsContainer = modal.querySelector('.wishlist-modal__variants');
    
    if (!variantsContainer || !variantsContainer.dataset.variants) return;
    
    const variantsData = JSON.parse(variantsContainer.dataset.variants);
    
    // Get all selected options
    const selectedOptions = [];
    const optionInputs = variantsContainer.querySelectorAll('input[type="radio"]:checked');
    
    optionInputs.forEach(input => {
      selectedOptions.push(input.value);
    });
    
    // Find matching variant
    const matchingVariant = variantsData.find(variant => {
      return variant.options.every((option, index) => option === selectedOptions[index]);
    });
    
    if (matchingVariant) {
      // Update the wishlist button with variant data
      const wishlistButton = modal.querySelector('.selectForWishlist');
      
      if (wishlistButton) {
        wishlistButton.dataset.productId = variantsContainer.dataset.productId;
        wishlistButton.dataset.variantId = matchingVariant.id;
        wishlistButton.dataset.productTitle = variantsContainer.dataset.productTitle;
        wishlistButton.dataset.handle = variantsContainer.dataset.handle;
        wishlistButton.dataset.image = variantsContainer.dataset.image;
        wishlistButton.dataset.price = matchingVariant.price;
        wishlistButton.dataset.variantTitle = matchingVariant.title;
        
        // Don't disable button based on availability - this is for wishlist, not purchasing
        wishlistButton.disabled = false;
        const svg = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
        </svg>`;
        wishlistButton.innerHTML = svg + ' Add to Wishlist';
      }
      
      // Update price display
      const priceContainer = modal.querySelector('.wishlist-modal__price');
      if (priceContainer && matchingVariant.price) {
        let priceHTML = `<div class="price">`;
        
        if (matchingVariant.compare_at_price && matchingVariant.compare_at_price > matchingVariant.price) {
          const savings = ((matchingVariant.compare_at_price - matchingVariant.price) / matchingVariant.compare_at_price * 100).toFixed(0);
          priceHTML += `
            <span class="price__regular">${this.formatMoney(matchingVariant.price)}</span>
            <span class="price__compare">${this.formatMoney(matchingVariant.compare_at_price)}</span>
            <span class="badge price__badge-sale">SAVE ${savings}%</span>
          `;
        } else {
          priceHTML += `<span class="price__regular">${this.formatMoney(matchingVariant.price)}</span>`;
        }

        priceHTML += `<span class="badge price__badge-sale" style="background-color: #E2419E; color: white;">${this.bfBadgeTagText}</span>`;
        
        priceHTML += `</div>`;
        priceContainer.innerHTML = priceHTML;
      }
    }
  }

  updateDescription(modal, description) {
    const descEl = modal.querySelector('.wishlist-modal__description');
    const shortTextEl = descEl.querySelector('.wishlist-modal__description-text');
    const fullTextEl = descEl.querySelector('.wishlist-modal__description-full');
    const showMoreBtn = descEl.querySelector('.wishlist-modal__show-more');
    
    // Create a temporary div to parse the HTML and get text content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = description;
    const textContent = (tempDiv.textContent || tempDiv.innerText || '').trim();
    
    // If description is empty or whitespace only, hide the section
    if (!textContent) {
      descEl.style.display = 'none';
      return;
    }
    
    // Character limit for truncation (adjust as needed)
    const charLimit = 250;
    
    if (textContent.length > charLimit) {
      // Description is long - truncate it
      const truncatedHTML = this.truncateHTML(description, charLimit);
      
      // Verify truncated HTML has actual text content
      const truncatedDiv = document.createElement('div');
      truncatedDiv.innerHTML = truncatedHTML;
      const truncatedText = (truncatedDiv.textContent || truncatedDiv.innerText || '').trim();
      
      // If truncation resulted in empty content, just show the full description
      if (!truncatedText) {
        shortTextEl.innerHTML = description;
        fullTextEl.style.display = 'none';
        showMoreBtn.style.display = 'none';
      } else {
        shortTextEl.innerHTML = truncatedHTML;
        fullTextEl.innerHTML = description;
        showMoreBtn.style.display = 'inline-block';
        fullTextEl.style.display = 'none';
        showMoreBtn.textContent = 'Show More';
      }
    } else {
      // Description is short - show it all
      shortTextEl.innerHTML = description;
      fullTextEl.style.display = 'none';
      showMoreBtn.style.display = 'none';
    }
    
    descEl.style.display = 'block';
  }
  
  truncateHTML(html, maxLength) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const text = tempDiv.textContent || tempDiv.innerText;
    
    if (text.length <= maxLength) {
      return html;
    }
    
    // Truncate and add ellipsis
    const truncatedText = text.substring(0, maxLength).trim() + '...';
    
    // Try to preserve basic HTML structure for the truncated content
    let result = '';
    let charCount = 0;
    
    function traverseNodes(node) {
      if (charCount >= maxLength) return;
      
      if (node.nodeType === Node.TEXT_NODE) {
        const remainingChars = maxLength - charCount;
        if (node.textContent.length <= remainingChars) {
          result += node.textContent;
          charCount += node.textContent.length;
        } else {
          result += node.textContent.substring(0, remainingChars) + '...';
          charCount = maxLength;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();
        result += `<${tagName}>`;
        
        for (let child of node.childNodes) {
          if (charCount >= maxLength) break;
          traverseNodes(child);
        }
        
        result += `</${tagName}>`;
      }
    }
    
    for (let child of tempDiv.childNodes) {
      if (charCount >= maxLength) break;
      traverseNodes(child);
    }
    
    return result;
  }
  
  toggleDescription(button) {
    const modal = button.closest('.wishlist-modal');
    const shortText = modal.querySelector('.wishlist-modal__description-text');
    const fullText = modal.querySelector('.wishlist-modal__description-full');
    
    if (fullText.style.display === 'none' || !fullText.style.display) {
      shortText.style.display = 'none';
      fullText.style.display = 'block';
      button.textContent = 'Show Less';
    } else {
      shortText.style.display = 'block';
      fullText.style.display = 'none';
      button.textContent = 'Show More';
    }
  }

  async handleAddToWishlist(button) {
    const modal = button.closest('.wishlist-modal');
    
    // Check if required variant options are selected
    if (!this.areRequiredVariantOptionsSelected(modal)) {
      this.handleErrorMessage(modal, this.getVariantSelectionMessage(modal));
      return;
    }
    
    const productId = button.dataset.productId;
    const variantId = button.dataset.variantId;
    const productTitle = button.dataset.productTitle;
    const productHandle = button.dataset.handle;
    const imageUrl = button.dataset.image;
    const price = button.dataset.price;
    const variantTitle = button.dataset.variantTitle || 'Default';
    
    // Create Swym wishlist data object (matching component-list-card-product.js format)
    const data = {
      epi: parseInt(variantId),
      empi: parseInt(productId),
      du: `${window.location.origin}/products/${productHandle}`,
      dt: productTitle,
      iu: imageUrl,
      pr: Number(price),
      vi: variantTitle,
    };
    
    try {
      // Add loading state
      button.classList.add('loading');
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      
      // Check if Swym is available
      if (window._swat && window._swat.addToWishList) {
        await new Promise((resolve, reject) => {
          window._swat.addToWishList(data, (success) => {
            if (success) {
              resolve();
            } else {
              reject(new Error('Wishlist API returned false'));
            }
          });
        });
        
        // Show success feedback
        button.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
        </svg> Added to Wishlist!`;
        button.style.background = '#4CAF50';
        
        // Update all wishlist buttons for this product to show saved state
        this.updateWishlistButtonStates(productId, variantId);
        
        // Show success notification
        this.showNotification('Item added to wishlist successfully!', 'success');
        
        // Close modal after 1 second
        setTimeout(() => {
          this.closeModal();
        }, 1000);
        
      } else {
        throw new Error('Wishlist functionality not available');
      }
      
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      
      // Show error feedback
      button.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
      </svg> Failed - Try Again`;
      button.style.background = '#dc3545';
      
      this.showNotification('Failed to add item to wishlist. Please try again.', 'error');
      
      // Reset button after delay
      setTimeout(() => {
        button.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
        </svg> Add to Wishlist`;
        button.style.background = '#000';
        button.disabled = false;
      }, 2000);
      
    } finally {
      button.classList.remove('loading');
      button.setAttribute('aria-busy', 'false');
    }
  }
  
  updateWishlistButtonStates(productId, variantId) {
    // Find all wishlist buttons on the page for this product
    const allWishlistButtons = document.querySelectorAll('button.wishlistButton');
    
    allWishlistButtons.forEach(button => {
      // Check if this button is for the same product (by checking parent product card)
      const productCard = button.closest('.card-product, .list-card-product');
      if (!productCard) return;
      
      // Find the backup-addToWishlist button that matches our variant
      const variantButtons = productCard.querySelectorAll('.backup-addToWishlist');
      let isThisProduct = false;
      
      variantButtons.forEach(variantBtn => {
        if (variantBtn.dataset.productId == productId && variantBtn.dataset.variantId == variantId) {
          isThisProduct = true;
          // Mark this variant button as saved
          variantBtn.classList.add('is-saved');
          variantBtn.setAttribute('disabled', 'true');
          variantBtn.setAttribute('aria-label', 'Saved to wishlist');
          
          const svg = variantBtn.querySelector('svg.wishlist-heart');
          if (svg) {
            svg.classList.add('is-filled');
          }
        }
      });
      
      // If any variant of this product is in wishlist, mark main button as saved
      if (isThisProduct) {
        button.classList.add('is-saved');
        const svg = button.querySelector('svg.wishlist-heart');
        if (svg) {
          svg.classList.add('is-filled');
        }
      }
    });
    
    // Also dispatch a custom event for other components to listen to
    document.dispatchEvent(new CustomEvent('wishlistItemAdded', {
      detail: { productId, variantId }
    }));
  }

  showNotification(message, type = 'error') {
    const className = `wishlist-modal-${type}-notification`;
    let notification = document.querySelector(`.${className}`);
    
    if (!notification) {
      notification = document.createElement('div');
      notification.className = className;
      notification.setAttribute('role', 'alert');
      notification.setAttribute('aria-live', 'polite');
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: ${type === 'success' ? '#28a745' : '#dc3545'};
        color: white;
        padding: 12px 16px;
        border-radius: 4px;
        z-index: 99999;
        font-size: 14px;
        max-width: 300px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        transform: translateX(100%);
        transition: transform 0.3s ease;
      `;
      document.body.appendChild(notification);
    }
    
    notification.textContent = message;
    notification.style.transform = 'translateX(0)';
    
    // Clear any existing timeout
    if (notification._timeout) {
      clearTimeout(notification._timeout);
    }
    
    notification._timeout = setTimeout(() => {
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (notification && notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 5000);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new WishlistModal();
  });
} else {
  new WishlistModal();
}

